import { User } from "../graphql/generated/graphql";
import { useSession } from "next-auth/react";
import UserInfoBadge from "./UserInfoBadge";
import { BsWhatsapp } from "react-icons/bs";
import { AiOutlineMessage, AiOutlinePhone } from "react-icons/ai";
import { EyeSlashIcon } from "@heroicons/react/24/solid";
import { event } from "../lib/gtag";
import useGetSellerInfo from "../hooks/useGetSellerInfo";
import { classNames } from "../utils/formatters";

type TProps = {
  seller: User;
  vehicleInfo?: Record<string, string | boolean>;
};

function getContactMessage(username, isYourIdVerified, vehicleInfo) {
  const msg = `¡Hola! Vi el carro ${
    vehicleInfo.title
  } en Movi. Quisiera saber si sigue disponible.
    ${username ? `\nMi nombre de usuario en Movi es: ${username}` : ""}
    \nLink: ${process.env.NEXT_PUBLIC_ROOT_URL}/autosusados/${vehicleInfo.slug}
    \n¡Gracias!`;
  const encodedMsg = encodeURIComponent(msg);
  return encodedMsg;
}

export default function ContactButtons({ seller, vehicleInfo }: TProps) {
  const { status: sessionStatus, data: sessionData } = useSession();
  const { user: currentUser } = sessionData || {};
  const isYourIdVerified = currentUser?.idVerified;
  const isContactInfoPublic = vehicleInfo?.isContactInfoPublic;
  const { sellerInfo, loading } = useGetSellerInfo({
    seller,
    isContactInfoPublic,
  });

  const { primaryPhone, secondaryPhone } = sellerInfo || {};

  const contactMsg = vehicleInfo
    ? getContactMessage(currentUser?.username, isYourIdVerified, vehicleInfo)
    : "";

  function onWhatsappClick() {
    event({
      category: "Contact",
      action: "click_seller_whatsapp",
      label: `${seller.username} - ${vehicleInfo.id}`,
    });
  }

  function onSMSClick() {
    event({
      category: "Contact",
      action: "click_seller_sms",
      label: `${seller.username} - ${vehicleInfo.id}`,
    });
  }

  function onCallClick() {
    event({
      category: "Contact",
      action: "click_seller_call",
      label: `${seller.username} - ${vehicleInfo.id}`,
    });
  }

  return (
    <div>
      <div>
        <UserInfoBadge user={sellerInfo} loading={loading} />
        {/* {!isYourIdVerified && sessionStatus === "authenticated" ? (
          <div className="border-t border-gray-200 mt-3 py-2 ">
            <div className="flex items-center justify-center gap-x-1">
              <VerifiedUserIcon
                idVerified={isYourIdVerified}
                className="h-7 w-7"
                triangle
              />
              <p className="text-lg">No has verificado tu identidad</p>
            </div>
            <p className="text-sm text-gray-600 mt-2 ml-2">
              Para dar más seguridad y confianza al vendedor,
              <Link href="/profile/verify">
                <span className="text-cyan-500 hover:text-cyan-400 inline-flex gap-x-1 items-center cursor-pointer font-medium">
                  verificá tu identidad
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 " />
                </span>
              </Link>
              . El vendedor puede solicitar tu nombre de usuario para verificar
              tu información.
            </p>
          </div>
        ) : null} */}
        {!loading ? (
          <div className="text-center mt-2">
            <h3 className="leading-6 text-gray-600">Contactar vendedor</h3>
            {sessionStatus === "unauthenticated" && !isContactInfoPublic ? (
              <div className="my-2 flex flex-col items-center">
                <EyeSlashIcon className="h-12 w-12 text-gray-500" />
                <p className="text-sm text-gray-500">
                  <span>
                    Este vendedor ha preferido que solo usuarios registrados
                    puedan contactarlo.
                  </span>
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {!loading ? (
        <div
          className={classNames(
            "grid gap-x-1 gap-y-2 mt-2",
            sessionStatus === "unauthenticated" && !isContactInfoPublic
              ? "grid-cols-1"
              : "",
            (isContactInfoPublic || sessionStatus === "authenticated") &&
              secondaryPhone &&
              secondaryPhone !== primaryPhone
              ? "grid-cols-2"
              : ""
          )}
        >
          {sessionStatus === "unauthenticated" && !isContactInfoPublic ? (
            <a
              type="button"
              className="inline-flex gap-x-1 w-full justify-center rounded-md border border-transparent bg-orange-500 px-4 py-2 text-sm md:text-base font-medium text-white shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 sm:text-sm disabled:opacity-70"
              href={`${window.origin}/auth/signin`}
              target="_blank"
              rel="noreferrer"
            >
              Iniciar sesión
            </a>
          ) : null}
          {(isContactInfoPublic || sessionStatus === "authenticated") &&
          secondaryPhone &&
          secondaryPhone !== primaryPhone ? (
            <span className="text-center text-sm text-gray-500">Primario</span>
          ) : null}
          {(isContactInfoPublic || sessionStatus === "authenticated") &&
          secondaryPhone &&
          secondaryPhone !== primaryPhone ? (
            <span className="text-center text-sm text-gray-500">
              Secundario
            </span>
          ) : null}
          {isContactInfoPublic ||
          (sessionStatus === "authenticated" &&
            secondaryPhone &&
            secondaryPhone !== primaryPhone) ? (
            <a
              id="seller-whatsapp-button"
              type="button"
              className="group inline-flex gap-x-1 w-full justify-center rounded-md border border-transparent bg-green-500 px-4 py-2 text-sm md:text-base lg:text-sm font-medium text-white shadow-sm hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:text-sm disabled:opacity-70"
              href={`https://wa.me/${primaryPhone}?text=${contactMsg}`}
              target="_blank"
              rel="noreferrer"
              onClick={onWhatsappClick}
            >
              <BsWhatsapp className="h-5 w-5" />
              <span className="inline group-hover:hidden">Whatsapp</span>
              <span className="hidden group-hover:inline">{primaryPhone}</span>
            </a>
          ) : null}
          {(isContactInfoPublic || sessionStatus === "authenticated") &&
          secondaryPhone &&
          secondaryPhone !== primaryPhone ? (
            <a
              id="seller-whatsapp-button-secondary"
              type="button"
              className=" group inline-flex gap-x-1 w-full justify-center rounded-md border border-transparent bg-green-500 px-4 py-2 text-sm md:text-base lg:text-sm font-medium text-white shadow-sm hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:text-sm disabled:opacity-70"
              href={`https://wa.me/${secondaryPhone}?text=${contactMsg}`}
              target="_blank"
              rel="noreferrer"
              onClick={onWhatsappClick}
            >
              <BsWhatsapp className="h-5 w-5" />
              <span className="inline group-hover:hidden">Whatsapp</span>
              <span className="hidden group-hover:inline">
                {secondaryPhone}
              </span>
            </a>
          ) : null}
          {isContactInfoPublic || sessionStatus === "authenticated" ? (
            <a
              id="seller-sms-button"
              type="button"
              className=" group inline-flex gap-x-1 w-full justify-center rounded-md border border-transparent bg-blue-500 px-4 py-2 text-sm md:text-base lg:text-sm font-medium text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:text-sm disabled:opacity-70"
              href={`sms:${primaryPhone}&body=${contactMsg}`}
              onClick={onSMSClick}
            >
              <AiOutlineMessage className="h-5 w-5" />
              <span className="inline group-hover:hidden">SMS</span>
              <span className="hidden group-hover:inline">{primaryPhone}</span>
            </a>
          ) : null}
          {(isContactInfoPublic || sessionStatus === "authenticated") &&
          secondaryPhone &&
          secondaryPhone !== primaryPhone ? (
            <a
              id="seller-sms-button-secondary"
              type="button"
              className=" group inline-flex gap-x-1 w-full justify-center rounded-md border border-transparent bg-blue-500 px-4 py-2 text-sm md:text-base lg:text-sm font-medium text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:text-sm disabled:opacity-70"
              href={`sms:${secondaryPhone}&body=${contactMsg}`}
              onClick={onSMSClick}
            >
              <AiOutlineMessage className="h-5 w-5" />
              <span className="inline group-hover:hidden">SMS</span>
              <span className="hidden group-hover:inline">
                {secondaryPhone}
              </span>
            </a>
          ) : null}
          {isContactInfoPublic || sessionStatus === "authenticated" ? (
            <a
              id="seller-call-button"
              type="button"
              className=" group inline-flex gap-x-1 w-full justify-center rounded-md border border-transparent bg-black px-4 py-2 text-sm md:text-base lg:text-sm font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 sm:text-sm disabled:opacity-70"
              href={`tel:${primaryPhone}`}
              onClick={onCallClick}
            >
              <AiOutlinePhone className="h-5 w-5" />
              <span className="inline group-hover:hidden">Llamar</span>
              <span className="hidden group-hover:inline">{primaryPhone}</span>
            </a>
          ) : null}
          {(isContactInfoPublic || sessionStatus === "authenticated") &&
          secondaryPhone &&
          secondaryPhone !== primaryPhone ? (
            <a
              id="seller-call-button-secondary"
              type="button"
              className=" group inline-flex gap-x-1 w-full justify-center rounded-md border border-transparent bg-black px-4 py-2 text-sm md:text-base lg:text-sm font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 sm:text-sm disabled:opacity-70"
              href={`tel:${secondaryPhone}`}
              onClick={onCallClick}
            >
              <AiOutlinePhone className="h-5 w-5" />
              <span className="inline group-hover:hidden">Llamar</span>
              <span className="hidden group-hover:inline">
                {secondaryPhone}
              </span>
            </a>
          ) : null}
          {/* <button
          type="button"
          className="mt-2 inline-flex w-full justify-center rounded-md border border-gray-300 hover:border-gray-400 bg-white px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-600 shadow-sm focus:outline-none focus:ring-2  focus:ring-offset-2 sm:text-sm"
          onClick={() => setOpen(false)}
        >
          Cancelar
        </button> */}
        </div>
      ) : null}
    </div>
  );
}
